import { DATA_TYPE_LABELS, type EdgeType, type Issue } from '@reelplicate/flows';
import { DataTypeChip } from '@reelplicate/ui';
import {
  BaseEdge,
  type Edge,
  EdgeLabelRenderer,
  type EdgeProps,
  getSmoothStepPath,
} from '@xyflow/react';
import { memo } from 'react';

import { chipType, countLabel } from '../catalogue-ui';

export type DataEdgeData = {
  /** What travels along it; undefined until something upstream decides. */
  type?: EdgeType | 'trigger';
  /** Estimated items (upper bound when a filter is upstream). */
  count?: number;
  error?: Issue;
};
export type FlowCanvasEdge = Edge<DataEdgeData, 'data'>;

/**
 * A typed connection: an orthogonal line with rounded corners and a Data type
 * chip at its middle naming what flows along it ("15 scripts"). Selected lines
 * go brand/strong; a type error goes danger and dashed; trigger edges are
 * dashed with no chip.
 */
export const DataEdge = memo(function DataEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  selected,
  data,
  markerEnd,
}: EdgeProps<FlowCanvasEdge>) {
  const [path, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 10,
  });
  const type = data?.type;
  const error = data?.error;
  const stroke = error
    ? 'var(--color-status-danger-fg)'
    : selected
      ? 'var(--color-brand-strong)'
      : 'var(--color-border-strong)';

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        markerEnd={markerEnd}
        interactionWidth={16}
        style={{
          stroke,
          strokeWidth: selected ? 2 : 1.5,
          strokeDasharray: error || type === 'trigger' ? '5 4' : undefined,
        }}
      />
      {type && type !== 'trigger' && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan pointer-events-auto absolute"
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            }}
          >
            <DataTypeChip type={chipType(type)} title={error?.message}>
              {data?.count !== undefined
                ? countLabel(data.count, type.base)
                : DATA_TYPE_LABELS[type.base]}
            </DataTypeChip>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
});
